import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { PrimaryButton, DefaultButton } from '@bingads-webui-react/fabric-control';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { useId } from '@fluentui/react-components';
import { MultiLineEllipsisText } from '@bingads-webui-react/multiline-ellipsis';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';

import { PromptEditor } from './components/prompt-editor';
import { getImageBackgroundGenerationViewStyles } from '../styles/image-background-generation-view-styles';
import { GenerationHistoryList } from './components/generation-history-list';
import { ImageShimmer, SpinnerWithMessage, ErrorMessageBar } from '../shared-componenets';
import { ExampleImageList, ExampleImageCell } from './components/example-image';

const HISTORY_LIST_MAX_LENGTH = 10;
const applyButtonIconProps = { iconName: 'Accept' };

const ViewImpl = ({
  imageUrlWithoutBackground,
  imageUrlWithGeneratedBackground,
  generateImageBackground,
  onApply,
  onClose,
  i18n,
  classes,
  logger,
}) => {
  const [promptValue, setPromptValue] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showLearnMore, setShowLearnMore] = useState(false);

  const onPromptChanged = useCallback((value) => {
    if (value !== promptValue) {
      setPromptValue(value);
    }
  }, [promptValue]);

  const onSelectExampleImage = useCallback((promptText) => {
    if (promptText && promptText !== promptValue) {
      setPromptValue(promptText);
      logger.logTrace('Copilot change image background - generation view - Static example image clicked', { promptText });
    }
  }, [logger, promptValue]);

  const [historyList, setHistoryList] = useState([{
    promptValue: '',
    imageUrl: imageUrlWithoutBackground,
    isBackgroundEmpty: true,
  }]);
  const [selectedHistoryIndex, setSelectedHistoryIndex] = useState(-1);

  const onSelectHistory = useCallback((historyData, index) => {
    const { promptValue: pv, imageUrl } = historyData || {};
    if (pv !== undefined && imageUrl) {
      setPromptValue(pv);
      setSelectedHistoryIndex(index);
    }
  }, []);

  const addNewHistory = useCallback((pPromptValue, generatedImageUrl) => {
    let newHistoryList = [...historyList];
    if (newHistoryList.length >= HISTORY_LIST_MAX_LENGTH) {
      newHistoryList = newHistoryList.slice(1);
    }
    newHistoryList.unshift({ promptValue: pPromptValue, imageUrl: generatedImageUrl });
    setHistoryList(newHistoryList);
    setSelectedHistoryIndex(0);
  }, [historyList]);

  const onGenerateImageBackground = useCallback(() => {
    setIsGenerating(true);
    setErrorMessage('');
    setShowLearnMore(false);
    generateImageBackground(promptValue).then((imgUrl) => {
      addNewHistory(promptValue, imgUrl);
    }).catch((error) => {
      logger.logTrace('Copilot change image background - generation view - generate image failed', { error, promptValue });
      setErrorMessage(i18n.getString(error.uxMessage));
      if (error.availableActions && error.availableActions.gotoDisallowedContent) {
        setShowLearnMore(true);
      }
    }).finally(() => {
      setIsGenerating(false);
    });
    // todo: error status
  }, [addNewHistory, generateImageBackground, i18n, logger, promptValue]);

  const curGeneratedImageUrl = useMemo(() => {
    if (selectedHistoryIndex >= 0 && selectedHistoryIndex < historyList.length) {
      return historyList[selectedHistoryIndex].imageUrl;
    }
    return imageUrlWithGeneratedBackground;
  }, [historyList, imageUrlWithGeneratedBackground, selectedHistoryIndex]);

  const shouldUploadCurGeneratedImage = useMemo(() => {
    if (selectedHistoryIndex >= 0 && selectedHistoryIndex < historyList.length) {
      return historyList[selectedHistoryIndex].isBackgroundEmpty;
    }
    return false;
  }, [historyList, selectedHistoryIndex]);

  const onApplyBackground = useCallback(() => {
    if (curGeneratedImageUrl) {
      setIsApplying(true);
      // onApply('https://www.bing-exp.com/th?id=OADD2.7146826644149_17CRXW80FIKIPFUKYY&pid=21.2&c=0').catch((/* err */) => {
      onApply(curGeneratedImageUrl, { shouldUpload: shouldUploadCurGeneratedImage }).then((image) => {
        const { processedImage } = image || {};
        logger.logTrace('Copilot change image background - generation view - Apply generated image button clicked', {
          imageId: processedImage.id,
          shouldUpload: shouldUploadCurGeneratedImage,
          prompt: promptValue,
          imageUrl: curGeneratedImageUrl,
        });
      }).catch((/* err */) => {
        // todo: error handling
      }).finally(() => {
        setIsApplying(false);
      });
    }
  }, [curGeneratedImageUrl, logger, onApply, shouldUploadCurGeneratedImage, promptValue]);

  useEffect(() => {
    if (promptValue && promptValue.length > 200) {
      setErrorMessage(i18n.getString(_TL_('Prompt must be less than 200 characters')));
    } else {
      setErrorMessage('');
    }
  }, [promptValue, i18n]);

  const DisallowedContent = useMemo(() => (showLearnMore ? (
    <LearnMoreLink
      linkText={i18n.getString(_TL_('Disallowed content'))}
      topic="app54862"
    />) : null
  ), [i18n, showLearnMore]);

  const exampleImageId = useId('example-image-list');

  return (
    <div className={classNames('background-change-view-container', 'image-background-generation-view')} >
      <div className="alert alert-info form-group">
        <div className="alert-image" />
        <div className="alert-info-inline">
          {i18n.getString(_TL_('Note that AI-generated content may not always be accurate. Please double-check, crop, and verify AI-generated content before using it.'))}
        </div>
      </div>
      <div className={classNames('background-change-view-content-container', classes.imageBackgroundGenerationView)}>
        <div className="background-change-view-left-panel">
          <div className="prompt-and-hisotry-container">
            <div className="prompt-editor-container">
              <PromptEditor
                disabled={isGenerating || isApplying}
                promptValue={promptValue}
                onPromptChanged={onPromptChanged}
                onGenerateImageBackground={onGenerateImageBackground}
                logger={logger}
                i18n={i18n}
              />
            </div>
            { isGenerating && (
              <div className="spinner-container">
                <SpinnerWithMessage message={i18n.getString(_TL_('Generating a new background...'))} />
              </div>
            )}
            { isApplying && (
              <div className="spinner-container">
                <SpinnerWithMessage message={i18n.getString(_TL_('Applying new image...'))} />
              </div>
            )}
            <ErrorMessageBar
              errorMessage={errorMessage}
              customClassName="error-message"
              extraAction={DisallowedContent}
            />
            {!isApplying && !isGenerating && historyList && historyList.length > 0 && (
              <div className="generation-history">
                <GenerationHistoryList
                  selectedIndex={selectedHistoryIndex}
                  historyList={historyList}
                  onSelectHistory={onSelectHistory}
                  i18n={i18n}
                />
              </div>
            )}
          </div>
          {!isGenerating && (
            <div className="background-change-view-actions-container">
              <PrimaryButton
                className="apply-generated-image-button"
                iconProps={applyButtonIconProps}
                onClick={() => {
                  onApplyBackground();
                }}
                disabled={!curGeneratedImageUrl || isApplying}
              >
                {i18n.getString(_TL_('Apply background'))}
              </PrimaryButton>
              <DefaultButton
                className="cancel-button"
                appearance="secondary"
                onClick={() => {
                  onClose();
                  logger.logTrace('Copilot change image background - generation view - Cancel button clicked');
                }}
                disabled={isApplying}
              >
                {i18n.getString(_TL_('Cancel'))}
              </DefaultButton>
            </div>
          )}
        </div>
        {isGenerating && (
          <div className={classes.generatingImageBackgroundContainer}>
            <ImageShimmer className="generating-image-shimmer" />
            <img src={imageUrlWithoutBackground} alt="Generating" />
          </div>
        )}
        {!isGenerating && !curGeneratedImageUrl && (
          <div className="example-image-list-container">
            <span className="example-image-list-label" id={exampleImageId}>{i18n.getString(_TL_('Try an example'))}</span>
            <ExampleImageList
              i18n={i18n}
              labelId={exampleImageId}
              MultiLineEllipsisText={MultiLineEllipsisText}
              ExampleImageCell={ExampleImageCell}
              onSelectImage={onSelectExampleImage}
            />
          </div>
        )}
        {!isGenerating && curGeneratedImageUrl && (
          <div className={classNames(classes.generatedImagePreviewContainer, 'generated-image-preview')}>
            <img src={curGeneratedImageUrl} alt="Generated" />
          </div>
        )}
      </div>
    </div>
  );
};

ViewImpl.propTypes = {
  imageUrlWithoutBackground: PropTypes.string,
  imageUrlWithGeneratedBackground: PropTypes.string,
  generateImageBackground: PropTypes.func.isRequired,
  onApply: PropTypes.func,
  onClose: PropTypes.func,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  classes: PropTypes.shape({}).isRequired,
};

ViewImpl.defaultProps = {
  imageUrlWithoutBackground: '',
  imageUrlWithGeneratedBackground: '',
  onApply: () => {},
  onClose: () => {},
};

export const ImageBackgroundGenerationView = withDefaultStyles(ViewImpl, getImageBackgroundGenerationViewStyles);
